/**
 * MailListBuilderView — build a tiered direct-mail household audience.
 *
 * The operator defines an audience (contest scope via candidates OR a geo
 * block, party filters, election, optional turnout floor), previews the tiered
 * household / voter / cost breakdown, then exports the cumulative CSV for a
 * chosen tier.
 *
 * Roles:
 *   - Preview is available to {ADMIN, ORG_MANAGER, MANAGER, CAMPAIGN_MANAGER,
 *     ANALYST}. ANALYST may preview but NOT export.
 *   - Bulk CSV export is {ADMIN, ORG_MANAGER, MANAGER} only. The download
 *     control is hidden for everyone else; the server enforces the same gate.
 *
 * Tier thresholds are PROVISIONAL — precinct r_lean × turnout propensity. They
 * are surfaced in an "Advanced" block and clearly labelled as such.
 *
 * Pattern mirrors LeadsView / CandidatesAdminView: axios + useState/useEffect
 * (not React Query), design-system tokens, ViewHeader with an `actions` slot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, Download, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  previewMailList,
  exportMailList,
  listCandidates,
  listMailListOptions,
  type AudienceSpec,
  type AudienceGeo,
  type MailListOptions,
  type MailListPreview,
  type MailListTier,
  type TierCount,
  type TotalsPreview,
  type ContestPreview,
} from '@/api/mailList';
import { triggerBrowserDownload } from '@/api/exports';
import type { CandidateListItem } from '@/types/candidate';
import { candidateShortName } from '@/types/candidate';
import { ViewHeader } from '@/components/ViewHeader';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Role gates
// ---------------------------------------------------------------------------

const PREVIEW_ROLES = new Set(['ADMIN', 'ORG_MANAGER', 'MANAGER', 'SMS_MANAGER', 'ANALYST']);
const EXPORT_ROLES = new Set(['ADMIN', 'ORG_MANAGER', 'MANAGER']);

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_ELECTION_DATE = '2026-08-04';
const DEFAULT_ELECTION_TYPE = 'primary';
const DEFAULT_PARTY_EXCLUDE = 'D';
const DEFAULT_PARTY_EXCLUDE_LIST: string[] = ['D'];
const DEFAULT_RATE_CENTS = 73;
const DEFAULT_T1 = 0.55;
const DEFAULT_T2 = 0.4;
const DEFAULT_T3 = 0.25;

const TIER_OPTIONS: Array<{ value: MailListTier; label: string }> = [
  { value: 't1', label: 'T1 only' },
  { value: 't1_2', label: 'T1 + T2' },
  { value: 't1_2_3', label: 'T1 + T2 + T3' },
];

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Cents → "$1,234" (whole dollars, rounded). */
function formatCents(cents: number): string {
  const dollars = Math.round((cents ?? 0) / 100);
  return `$${dollars.toLocaleString('en-US')}`;
}

/** Cents → "0.73" for the per-piece rate input (dollars, 2dp). */
function centsToDollarString(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Parse a "0.73" dollar string back into integer cents; NaN → fallback. */
function dollarStringToCents(value: string, fallback: number): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.round(parsed * 100);
}

function formatCount(n: number): string {
  return (n ?? 0).toLocaleString('en-US');
}

/** Split a comma/whitespace/newline separated list into trimmed non-empty tokens. */
function splitTokens(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** Toggle membership of `value` in an array (add if absent, remove if present). */
function toggleValue(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface MailListBuilderViewProps {
  userRole?: string;
}

export function MailListBuilderView({ userRole }: MailListBuilderViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  const canPreview = roleMatches(roleNorm, PREVIEW_ROLES);
  const canExport = roleMatches(roleNorm, EXPORT_ROLES);
  const isMobile = useIsMobile();

  // ---- Form state ---------------------------------------------------------
  const [electionDate, setElectionDate] = useState(DEFAULT_ELECTION_DATE);
  const [electionType, setElectionType] = useState(DEFAULT_ELECTION_TYPE);

  const [candidates, setCandidates] = useState<CandidateListItem[]>([]);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([]);

  // Filter options for the dropdowns. `null` until loaded; when `optionsError`
  // is set we fall back to the free-text inputs so the view still works.
  const [options, setOptions] = useState<MailListOptions | null>(null);
  const [optionsError, setOptionsError] = useState(false);
  const optionsReady = options !== null && !optionsError;

  // Free-text fallback state (used only when options fail to load).
  const [precinctsRaw, setPrecinctsRaw] = useState('');
  const [subregionsRaw, setSubregionsRaw] = useState('');
  const [ldsRaw, setLdsRaw] = useState('');
  const [partyExcludeRaw, setPartyExcludeRaw] = useState(DEFAULT_PARTY_EXCLUDE);

  // Dropdown selection state (used when options load successfully).
  const [selectedPrecincts, setSelectedPrecincts] = useState<string[]>([]);
  const [selectedSubregions, setSelectedSubregions] = useState<string[]>([]);
  const [selectedLds, setSelectedLds] = useState<string[]>([]);
  const [selectedPartyExclude, setSelectedPartyExclude] =
    useState<string[]>(DEFAULT_PARTY_EXCLUDE_LIST);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [t1, setT1] = useState(String(DEFAULT_T1));
  const [t2, setT2] = useState(String(DEFAULT_T2));
  const [t3, setT3] = useState(String(DEFAULT_T3));
  const [rateDollars, setRateDollars] = useState(centsToDollarString(DEFAULT_RATE_CENTS));

  const [turnoutEnabled, setTurnoutEnabled] = useState(false);
  const [turnoutK, setTurnoutK] = useState('2');
  const [turnoutN, setTurnoutN] = useState('4');

  // ---- Request state ------------------------------------------------------
  const [preview, setPreview] = useState<MailListPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [exportTier, setExportTier] = useState<MailListTier>('t1_2');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Load candidates for the multi-select (best-effort — geo mode works without).
  useEffect(() => {
    if (!canPreview) return;
    let cancelled = false;
    listCandidates(true)
      .then((rows) => {
        if (cancelled) return;
        setCandidates(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setCandidatesError('Could not load candidates — use the geography filters instead.');
      });
    return () => {
      cancelled = true;
    };
  }, [canPreview]);

  // Load the filter dropdown options (best-effort — on failure we fall back to
  // the free-text inputs).
  useEffect(() => {
    if (!canPreview) return;
    let cancelled = false;
    listMailListOptions()
      .then((opts) => {
        if (cancelled) return;
        setOptions(opts);
        setOptionsError(false);
        // Keep the current election type selected if it's offered; otherwise
        // fall back to the first available option so the select is never blank.
        setElectionType((cur) =>
          opts.election_types.length === 0 || opts.election_types.includes(cur)
            ? cur
            : opts.election_types[0],
        );
      })
      .catch(() => {
        if (cancelled) return;
        setOptionsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canPreview]);

  const buildSpec = useCallback((): AudienceSpec => {
    const precincts = optionsReady ? selectedPrecincts : splitTokens(precinctsRaw);
    const subregions = optionsReady ? selectedSubregions : splitTokens(subregionsRaw);
    const lds = optionsReady ? selectedLds : splitTokens(ldsRaw);
    const geo: AudienceGeo = {};
    if (precincts.length) geo.precincts = precincts;
    if (subregions.length) geo.subregions = subregions;
    if (lds.length) geo.legislative_districts = lds;

    const partyExclude = optionsReady
      ? selectedPartyExclude
      : splitTokens(partyExcludeRaw).map((p) => p.toUpperCase());

    const spec: AudienceSpec = {
      election_date: electionDate,
      election_type: electionType,
      party_exclude: partyExclude,
      tier_thresholds: {
        t1: Number.parseFloat(t1) || DEFAULT_T1,
        t2: Number.parseFloat(t2) || DEFAULT_T2,
        t3: Number.parseFloat(t3) || DEFAULT_T3,
      },
      per_piece_rate_cents: dollarStringToCents(rateDollars, DEFAULT_RATE_CENTS),
    };

    if (selectedCandidateIds.length > 0) {
      spec.candidate_ids = selectedCandidateIds;
    } else if (Object.keys(geo).length > 0) {
      spec.geo = geo;
    }

    if (turnoutEnabled) {
      const k = Number.parseInt(turnoutK, 10);
      const n = Number.parseInt(turnoutN, 10);
      if (Number.isFinite(k) && Number.isFinite(n) && n > 0) {
        spec.turnout_min = { k, n };
      }
    }

    return spec;
  }, [
    optionsReady,
    precinctsRaw,
    subregionsRaw,
    ldsRaw,
    partyExcludeRaw,
    selectedPrecincts,
    selectedSubregions,
    selectedLds,
    selectedPartyExclude,
    electionDate,
    electionType,
    t1,
    t2,
    t3,
    rateDollars,
    selectedCandidateIds,
    turnoutEnabled,
    turnoutK,
    turnoutN,
  ]);

  const hasScope = useMemo(() => {
    if (selectedCandidateIds.length > 0) return true;
    if (optionsReady) {
      return (
        selectedPrecincts.length > 0 ||
        selectedSubregions.length > 0 ||
        selectedLds.length > 0
      );
    }
    return (
      splitTokens(precinctsRaw).length > 0 ||
      splitTokens(subregionsRaw).length > 0 ||
      splitTokens(ldsRaw).length > 0
    );
  }, [
    selectedCandidateIds,
    optionsReady,
    selectedPrecincts,
    selectedSubregions,
    selectedLds,
    precinctsRaw,
    subregionsRaw,
    ldsRaw,
  ]);

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExportError(null);
    try {
      const result = await previewMailList(buildSpec());
      setPreview(result);
    } catch (e: unknown) {
      const status =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined;
      setError(
        status === 403
          ? 'Your role does not have access to the mail-list preview.'
          : status === 422
            ? 'The audience definition was rejected (422). Check your geography and election fields.'
            : status
              ? `Backend error ${status}.`
              : 'Could not reach the backend.',
      );
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [buildSpec]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportMailList(buildSpec(), exportTier);
      triggerBrowserDownload(blob, `mail-list-${exportTier}.csv`);
    } catch (e: unknown) {
      const status =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined;
      setExportError(
        status === 403
          ? 'Bulk export is limited to managers and administrators.'
          : status
            ? `Export failed (${status}).`
            : 'Export failed — could not reach the backend.',
      );
    } finally {
      setExporting(false);
    }
  }, [buildSpec, exportTier]);

  const toggleCandidate = useCallback((id: number) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // ---- Access wall --------------------------------------------------------
  if (!canPreview) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            The Mail List Builder is available to campaign managers, managers, and administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-accent" />
            Mail List Builder
          </span>
        }
        tagline="Tiered household targeting for direct mail — preview counts and cost, then export a CSV."
        actions={
          canExport ? (
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="mail-export-tier">
                Export tier
              </label>
              <select
                id="mail-export-tier"
                value={exportTier}
                onChange={(e) => setExportTier(e.target.value as MailListTier)}
                className="bg-bg-base border border-border-base rounded px-2 py-2 text-fg-primary text-xs"
                title="Which cumulative tier bundle to export"
              >
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || !hasScope}
                className="inline-flex items-center gap-1.5 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-2"
                title={hasScope ? 'Export the audience CSV' : 'Define a contest or geography first'}
              >
                {exporting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                {exporting ? 'Preparing…' : 'Download CSV'}
              </button>
            </div>
          ) : null
        }
      />

      {exportError && (
        <div className="bg-status-danger-bg/30 rounded-xl p-3 border border-status-danger-bg text-status-danger text-sm">
          {exportError}
        </div>
      )}

      {/* Filter form */}
      <div className="bg-bg-raised rounded-xl border border-border-base p-4 sm:p-5 space-y-5">
        {/* Election */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Election date">
            <input
              type="date"
              value={electionDate}
              onChange={(e) => setElectionDate(e.target.value)}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            />
          </Field>
          <Field label="Election type">
            {optionsReady && options ? (
              <select
                value={electionType}
                onChange={(e) => setElectionType(e.target.value)}
                className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              >
                {options.election_types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={electionType}
                onChange={(e) => setElectionType(e.target.value)}
                placeholder="primary"
                className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              />
            )}
          </Field>
        </div>

        {/* Contest scope: candidates */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1.5">
            Contest scope — candidates
          </div>
          {candidatesError ? (
            <p className="text-xs text-status-warning">{candidatesError}</p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-fg-muted">No active candidates found — use geography below.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {candidates.map((c) => {
                const active = selectedCandidateIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCandidate(c.id)}
                    aria-pressed={active}
                    className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                      active
                        ? 'bg-accent text-white border-accent'
                        : 'bg-bg-base text-fg-secondary border-border-base hover:border-border-strong'
                    }`}
                    title={`${c.office}${c.district_label ? ` · ${c.district_label}` : ''}`}
                  >
                    {candidateShortName(c)}
                    {c.district_label ? (
                      <span className={active ? 'opacity-80' : 'text-fg-muted'}> · {c.district_label}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-fg-muted mt-1.5">
            Pick one or more contests, or leave empty and target by geography instead.
          </p>
        </div>

        {/* Geo scope — only meaningful when no candidates selected */}
        {(() => {
          const geoDisabled = selectedCandidateIds.length > 0;
          return (
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${geoDisabled ? 'opacity-50' : ''}`}>
              {optionsReady && options ? (
                <>
                  <Field label="Precincts">
                    <ChipMultiSelect
                      values={options.precincts}
                      getKey={(v) => v}
                      getLabel={(v) => v}
                      selected={selectedPrecincts}
                      onToggle={(v) => setSelectedPrecincts((prev) => toggleValue(prev, v))}
                      disabled={geoDisabled}
                      scroll
                      emptyText="No precincts available."
                    />
                  </Field>
                  <Field label="Subregions">
                    <ChipMultiSelect
                      values={options.subregions}
                      getKey={(s) => s.key}
                      getLabel={(s) => s.label}
                      selected={selectedSubregions}
                      onToggle={(v) => setSelectedSubregions((prev) => toggleValue(prev, v))}
                      disabled={geoDisabled}
                      scroll
                      emptyText="No subregions available."
                    />
                  </Field>
                  <Field label="Legislative districts">
                    <ChipMultiSelect
                      values={options.legislative_districts}
                      getKey={(v) => v}
                      getLabel={(v) => v}
                      selected={selectedLds}
                      onToggle={(v) => setSelectedLds((prev) => toggleValue(prev, v))}
                      disabled={geoDisabled}
                      emptyText="No legislative districts available."
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Precincts">
                    <input
                      type="text"
                      value={precinctsRaw}
                      onChange={(e) => setPrecinctsRaw(e.target.value)}
                      placeholder="e.g. 8, 35, 13"
                      disabled={geoDisabled}
                      className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm disabled:cursor-not-allowed"
                    />
                  </Field>
                  <Field label="Subregions">
                    <input
                      type="text"
                      value={subregionsRaw}
                      onChange={(e) => setSubregionsRaw(e.target.value)}
                      placeholder="turf keys, comma-separated"
                      disabled={geoDisabled}
                      className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm disabled:cursor-not-allowed"
                    />
                  </Field>
                  <Field label="Legislative districts">
                    <input
                      type="text"
                      value={ldsRaw}
                      onChange={(e) => setLdsRaw(e.target.value)}
                      placeholder="e.g. 12, 14"
                      disabled={geoDisabled}
                      className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm disabled:cursor-not-allowed"
                    />
                  </Field>
                </>
              )}
            </div>
          );
        })()}

        {/* Party filter */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Party exclude">
            {optionsReady && options ? (
              <>
                <ChipMultiSelect
                  values={options.party_codes}
                  getKey={(v) => v}
                  getLabel={(v) => v}
                  selected={selectedPartyExclude}
                  onToggle={(v) => setSelectedPartyExclude((prev) => toggleValue(prev, v))}
                  emptyText="No party codes available."
                />
                <span className="text-[11px] text-fg-muted mt-1 block">
                  Party codes to drop from the audience. Defaults to Democrats.
                </span>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={partyExcludeRaw}
                  onChange={(e) => setPartyExcludeRaw(e.target.value)}
                  placeholder="D"
                  className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
                />
                <span className="text-[11px] text-fg-muted mt-1 block">
                  Comma-separated party codes to drop. Defaults to Democrats.
                </span>
              </>
            )}
          </Field>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1.5">
              Turnout floor (optional)
            </div>
            <label className="flex items-center gap-2 text-sm text-fg-secondary">
              <input
                type="checkbox"
                checked={turnoutEnabled}
                onChange={(e) => setTurnoutEnabled(e.target.checked)}
                className="accent-accent"
              />
              Require voted in at least
              <input
                type="number"
                min={0}
                value={turnoutK}
                onChange={(e) => setTurnoutK(e.target.value)}
                disabled={!turnoutEnabled}
                className="w-14 bg-bg-base border border-border-base rounded px-2 py-1 text-fg-primary text-sm disabled:opacity-50"
              />
              of
              <input
                type="number"
                min={1}
                value={turnoutN}
                onChange={(e) => setTurnoutN(e.target.value)}
                disabled={!turnoutEnabled}
                className="w-14 bg-bg-base border border-border-base rounded px-2 py-1 text-fg-primary text-sm disabled:opacity-50"
              />
            </label>
          </div>
        </div>

        {/* Advanced */}
        <div className="border-t border-border-base pt-4">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-fg-secondary hover:text-fg-primary"
          >
            {advancedOpen ? (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            )}
            Advanced — tier thresholds &amp; per-piece rate
          </button>
          {advancedOpen && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="T1 cutoff">
                  <ScoreInput value={t1} onChange={setT1} />
                </Field>
                <Field label="T2 cutoff">
                  <ScoreInput value={t2} onChange={setT2} />
                </Field>
                <Field label="T3 cutoff">
                  <ScoreInput value={t3} onChange={setT3} />
                </Field>
                <Field label="Per-piece rate ($)">
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={rateDollars}
                    onChange={(e) => setRateDollars(e.target.value)}
                    className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-fg-muted">
                Tier thresholds are provisional — a household&apos;s tier comes from precinct r_lean ×
                turnout propensity. Adjust with care.
              </p>
            </div>
          )}
        </div>

        {/* Preview button */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading || !hasScope}
            className="inline-flex items-center gap-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {loading ? 'Previewing…' : 'Preview'}
          </button>
          {!hasScope && (
            <span className="text-xs text-fg-muted">Select a contest or enter a geography to preview.</span>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={handlePreview}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && !preview && (
        <div className="bg-bg-raised rounded-xl border border-dashed border-border-base p-8 text-center">
          <Mail className="w-8 h-8 text-fg-muted mx-auto mb-2" aria-hidden="true" />
          <p className="text-fg-secondary text-sm">
            Define an audience above and hit <span className="font-semibold">Preview</span> to see tiered
            household counts and estimated mail cost.
          </p>
        </div>
      )}

      {/* Results */}
      {preview && !loading && (
        <PreviewResults preview={preview} isMobile={isMobile} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results table / cards
// ---------------------------------------------------------------------------

function PreviewResults({ preview, isMobile }: { preview: MailListPreview; isMobile: boolean }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-fg-primary">Tiered audience</h2>
        <div className="text-xs text-fg-muted">
          Rate {formatCents(preview.per_piece_rate_cents)} / piece · cutoffs T1 ≥{' '}
          {preview.tier_thresholds.t1} · T2 ≥ {preview.tier_thresholds.t2} · T3 ≥{' '}
          {preview.tier_thresholds.t3}
        </div>
      </div>

      {isMobile ? (
        <div className="space-y-3">
          {preview.contests.map((c) => (
            <ContestCard key={c.key} contest={c} />
          ))}
          <TotalsCard totals={preview.totals} />
        </div>
      ) : (
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left text-fg-muted">
                  <th className="py-2.5 px-4 font-semibold">Contest</th>
                  <th className="py-2.5 px-4 font-semibold text-right">T1 HH</th>
                  <th className="py-2.5 px-4 font-semibold text-right">+T2 HH</th>
                  <th className="py-2.5 px-4 font-semibold text-right">+T3 HH</th>
                  <th className="py-2.5 px-4 font-semibold text-right">T1 cost</th>
                  <th className="py-2.5 px-4 font-semibold text-right">T1+2 cost</th>
                  <th className="py-2.5 px-4 font-semibold text-right">T1+2+3 cost</th>
                </tr>
              </thead>
              <tbody>
                {preview.contests.map((c) => (
                  <tr key={c.key} className="border-b border-border-base last:border-0 hover:bg-bg-base/40">
                    <td className="py-2.5 px-4 text-fg-primary font-medium">{c.label}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(c.t1.households)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(c.t2_add.households)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(c.t3_add.households)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                      {formatCents(c.cumulative.t1.cost_cents)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                      {formatCents(c.cumulative.t1_2.cost_cents)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                      {formatCents(c.cumulative.t1_2_3.cost_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border-strong bg-bg-base/40 font-semibold">
                  <td className="py-2.5 px-4 text-fg-primary">Totals</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCount(preview.totals.t1.households)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCount(preview.totals.t2_add.households)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCount(preview.totals.t3_add.households)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCents(preview.totals.cumulative.t1.cost_cents)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCents(preview.totals.cumulative.t1_2.cost_cents)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary">
                    {formatCents(preview.totals.cumulative.t1_2_3.cost_cents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-fg-muted">
        HH = households. &ldquo;+T2&rdquo; / &ldquo;+T3&rdquo; are the incremental households a wider tier
        adds; costs are cumulative bundles. Tier thresholds are provisional (precinct r_lean × turnout
        propensity).
      </p>
    </div>
  );
}

function ContestCard({ contest }: { contest: ContestPreview }) {
  return (
    <div className="bg-bg-raised rounded-xl border border-border-base p-4">
      <div className="text-fg-primary font-semibold mb-2">{contest.label}</div>
      <TierRows t1={contest.t1} t2Add={contest.t2_add} t3Add={contest.t3_add} contest={contest} />
    </div>
  );
}

function TotalsCard({ totals }: { totals: TotalsPreview }) {
  return (
    <div className="bg-bg-base rounded-xl border-2 border-border-strong p-4">
      <div className="text-fg-primary font-bold mb-2">Totals</div>
      <TierRows t1={totals.t1} t2Add={totals.t2_add} t3Add={totals.t3_add} contest={totals} />
    </div>
  );
}

function TierRows({
  t1,
  t2Add,
  t3Add,
  contest,
}: {
  t1: TierCount;
  t2Add: TierCount;
  t3Add: TierCount;
  contest: ContestPreview | TotalsPreview;
}) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      <dt className="text-fg-muted">T1 households</dt>
      <dd className="text-right tabular-nums text-fg-secondary">{formatCount(t1.households)}</dd>
      <dt className="text-fg-muted">+T2 households</dt>
      <dd className="text-right tabular-nums text-fg-secondary">{formatCount(t2Add.households)}</dd>
      <dt className="text-fg-muted">+T3 households</dt>
      <dd className="text-right tabular-nums text-fg-secondary">{formatCount(t3Add.households)}</dd>
      <dt className="text-fg-muted pt-1 border-t border-border-base mt-1">T1 cost</dt>
      <dd className="text-right tabular-nums text-fg-primary pt-1 border-t border-border-base mt-1">
        {formatCents(contest.cumulative.t1.cost_cents)}
      </dd>
      <dt className="text-fg-muted">T1+2 cost</dt>
      <dd className="text-right tabular-nums text-fg-primary">
        {formatCents(contest.cumulative.t1_2.cost_cents)}
      </dd>
      <dt className="text-fg-muted">T1+2+3 cost</dt>
      <dd className="text-right tabular-nums text-fg-primary">
        {formatCents(contest.cumulative.t1_2_3.cost_cents)}
      </dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Small field primitives
// ---------------------------------------------------------------------------

/**
 * A wrap-flowing group of toggle chips for multi-selecting from a list of
 * options. Mirrors the candidate chip group's look. Generic over the option
 * type so it can back both `string[]` (precincts, LDs, party codes) and
 * `{ key, label }[]` (subregions). `getKey` yields the value stored in the
 * `selected` array; `getLabel` yields the visible text.
 */
function ChipMultiSelect<T>({
  values,
  getKey,
  getLabel,
  selected,
  onToggle,
  disabled = false,
  scroll = false,
  emptyText,
}: {
  values: T[];
  getKey: (value: T) => string;
  getLabel: (value: T) => string;
  selected: string[];
  onToggle: (key: string) => void;
  disabled?: boolean;
  scroll?: boolean;
  emptyText?: string;
}) {
  if (values.length === 0) {
    return <p className="text-xs text-fg-muted">{emptyText ?? 'No options available.'}</p>;
  }
  return (
    <div
      className={`flex flex-wrap gap-1.5 ${scroll ? 'max-h-36 overflow-y-auto pr-1' : ''}`}
      role="group"
    >
      {values.map((v) => {
        const key = getKey(v);
        const active = selected.includes(key);
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onToggle(key)}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors disabled:cursor-not-allowed ${
              active
                ? 'bg-accent text-white border-accent'
                : 'bg-bg-base text-fg-secondary border-border-base hover:border-border-strong'
            }`}
          >
            {getLabel(v)}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">{label}</span>
      {children}
    </label>
  );
}

function ScoreInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="number"
      step="0.01"
      min={0}
      max={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
    />
  );
}

export default MailListBuilderView;
